import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, TrendingUp, TrendingDown, Calendar, FileUp, Pencil, Trash2, MoreVertical, Plus, ArrowUpCircle, ArrowDownCircle, Wallet } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ImportStatementModal } from "@/components/import/ImportStatementModal";
import { TransactionFiltersComponent, TransactionFilters, defaultFilters } from "@/components/transactions/TransactionFilters";
import { TransactionModal } from "@/components/transactions/TransactionModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Transactions = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [filters, setFilters] = useState<TransactionFilters>(() => {
    const accountId = searchParams.get("accountId");
    return {
      ...defaultFilters,
      accountIds: accountId ? [accountId] : [],
    };
  });
  
  // Edit/Delete state
  const [editingTransaction, setEditingTransaction] = useState<any | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteTransaction, setDeleteTransaction] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    // Debounce server-side search to avoid hammering on every keystroke
    const timer = setTimeout(() => {
      fetchTransactions();
    }, 500);
    return () => clearTimeout(timer);
  }, [filters]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      
      // Server-side filtering via RPC replaces client-side .filter()
      const { data, error } = await supabase.rpc("search_transactions", {
        p_search: filters.search || null,
        p_account_ids: filters.accountIds.length > 0 ? filters.accountIds : null,
        p_types: filters.types.length > 0 ? filters.types : null,
        p_category_id: filters.categoryId || null,
        p_is_paid: filters.isPaid !== "all" ? filters.isPaid : null,
        p_start_date: filters.startDate ? format(filters.startDate, "yyyy-MM-dd") : null,
        p_end_date: filters.endDate ? format(filters.endDate, "yyyy-MM-dd") : null,
        p_min_amount: filters.minAmount ? Number(filters.minAmount) : null,
        p_max_amount: filters.maxAmount ? Number(filters.maxAmount) : null,
        p_limit: 500,
      });

      if (error) throw error;
      setTransactions((data as any[]) || []);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao carregar transações",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Transactions are already filtered server-side
  const filteredTransactions = transactions;

  const TRANSFER_INCOMING_CATEGORY = "00000000-0000-0000-0000-000000000102";
  const isIncoming = (t: any) =>
    t.type === "income" || (t.type === "transfer" && t.category_id === TRANSFER_INCOMING_CATEGORY);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd MMM yyyy", { locale: ptBR });
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
  };

  const handleEdit = (transaction: any) => {
    setEditingTransaction(transaction);
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTransaction) return;
    
    setDeleting(true);
    try {
      const { error } = await supabase
        .from("transactions")
        .delete()
        .eq("id", deleteTransaction.id);

      if (error) throw error;

      toast({
        title: "Transação excluída",
        description: "A transação foi removida com sucesso.",
      });
      
      fetchTransactions();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao excluir",
        description: error.message,
      });
    } finally {
      setDeleting(false);
      setDeleteTransaction(null);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 overflow-x-hidden">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold">Transações</h1>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowEditModal(true)}>
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Adicionar Transação</span>
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowImportModal(true)}>
            <FileUp className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Importar Extrato</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <TransactionFiltersComponent
        filters={filters}
        onFiltersChange={setFilters}
        onClear={clearFilters}
      />

      {/* Results count + Summary indicators */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          {filteredTransactions.length} transação(ões) encontrada(s)
          {filters.search || filters.categoryId || filters.accountIds.length > 0 || filters.types.length > 0 || filters.startDate || filters.endDate || filters.minAmount || filters.maxAmount || filters.isPaid !== "all"
            ? ` de ${transactions.length} total`
            : ""}
        </div>
        {filteredTransactions.length > 0 && (
          <div className="flex items-center gap-3 text-sm flex-wrap">
            <div className="flex items-center gap-1.5 text-success">
              <ArrowUpCircle className="h-4 w-4" />
              <span className="font-medium">
                {formatCurrency(
                  filteredTransactions
                    .filter((t: any) => t.type === "income")
                    .reduce((sum: number, t: any) => sum + Number(t.amount), 0)
                )}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-destructive">
              <ArrowDownCircle className="h-4 w-4" />
              <span className="font-medium">
                {formatCurrency(
                  filteredTransactions
                    .filter((t: any) => t.type === "expense")
                    .reduce((sum: number, t: any) => sum + Number(t.amount), 0)
                )}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Wallet className="h-4 w-4" />
              <span className="font-medium">
                {formatCurrency(
                  filteredTransactions
                    .filter((t: any) => t.type !== "transfer")
                    .reduce(
                      (sum: number, t: any) =>
                        sum + (t.type === "income" ? Number(t.amount) : -Number(t.amount)),
                      0
                    )
                )}
              </span>
            </div>
          </div>
        )}
      </div>

      {filteredTransactions.length === 0 ? (
        <Card className="p-8 text-center">
          <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">
            {transactions.length === 0 ? "Nenhuma transação ainda" : "Nenhuma transação encontrada"}
          </h3>
          <p className="text-muted-foreground mb-4">
            {transactions.length === 0
              ? "Comece adicionando sua primeira transação"
              : "Tente ajustar os filtros para encontrar transações"}
          </p>
          {transactions.length > 0 && (
            <Button variant="outline" onClick={clearFilters}>
              Limpar Filtros
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredTransactions.map((transaction) => (
            <Card key={transaction.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: transaction.category?.color + "20",
                        color: transaction.category?.color,
                      }}
                    >
                      {transaction.type === "income" ? (
                        <TrendingUp className="h-5 w-5" />
                      ) : (
                        <TrendingDown className="h-5 w-5" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{transaction.description?.toUpperCase()}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap min-w-0">
                        <span className="truncate">{transaction.category?.name || "Sem categoria"}</span>
                        <span className="hidden sm:inline">•</span>
                        <span className="whitespace-nowrap">{formatDate(transaction.transaction_date)}</span>
                        {transaction.account && (
                          <>
                            <span className="hidden sm:inline">•</span>
                            <Badge
                              variant="outline"
                              className="text-xs px-1.5 py-0 h-5 font-normal truncate max-w-[100px] sm:max-w-none"
                              style={{
                                borderColor: transaction.account.color,
                                color: transaction.account.color,
                              }}
                            >
                              {transaction.account.name}
                            </Badge>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                    <div className="text-right">
                      <p
                        className={`font-bold text-sm sm:text-lg whitespace-nowrap ${
                          isIncoming(transaction) ? "text-success" : "text-destructive"
                        }`}
                      >
                        {isIncoming(transaction) ? "+" : "-"}
                        {formatCurrency(Number(transaction.amount))}
                      </p>
                      {!transaction.is_paid && (
                        <span className="text-xs text-muted-foreground">Pendente</span>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(transaction)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => setDeleteTransaction(transaction)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ImportStatementModal
        open={showImportModal}
        onOpenChange={setShowImportModal}
        onSuccess={fetchTransactions}
      />

      {/* Edit Transaction Modal */}
      <TransactionModal
        open={showEditModal}
        onOpenChange={(open) => {
          setShowEditModal(open);
          if (!open) setEditingTransaction(null);
        }}
        onSuccess={fetchTransactions}
        transaction={editingTransaction}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTransaction} onOpenChange={(open) => !open && setDeleteTransaction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir transação</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                Tem certeza que deseja excluir esta transação?
                {deleteTransaction && (
                  <div className="mt-2 p-3 bg-muted rounded-lg">
                    <p className="font-medium">{deleteTransaction.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatCurrency(Number(deleteTransaction.amount))} • {formatDate(deleteTransaction.transaction_date)}
                    </p>
                  </div>
                )}
                <p className="mt-2 text-sm">Esta ação não pode ser desfeita.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                "Excluir"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Transactions;
